import { useSelector } from "react-redux";
import React from "react";
import { useAppDispatch } from "../../app/hooks";
import { RootState } from "../../app/store";
import {
  CHESS_SETUPS,
  CUSTOM_SETUP_ID,
  getSetupById,
  getSetupsByCategory,
  StaticPositionSetup,
} from "../../models/SetupOptions";
import { setSelectedSetup } from "../../reducers/setups/setups.actions";
import "./SelectPosition.css";

interface SelectPositionProps {
  onCustomSelect?: () => void;
}

const SelectPosition: React.FC<SelectPositionProps> = ({ onCustomSelect }) => {
  const dispatch = useAppDispatch();
  const selectedSetup = useSelector((state: RootState) => state.selectedSetup);

  const handleOptionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const setupId = event.target.value;

    // When "Custom" is selected, switch to custom mode and focus the FEN input
    if (setupId === CUSTOM_SETUP_ID) {
      dispatch(setSelectedSetup(CUSTOM_SETUP_ID));
      onCustomSelect?.();
      return;
    }

    const setup = getSetupById(setupId);

    if (setup) {
      dispatch(setSelectedSetup(setupId));

      // For non-static setups (puzzles), trigger the data fetch
      // Static setups are handled by the useEffect in useChessGame
      if (!(setup instanceof StaticPositionSetup)) {
        setup.load(dispatch);
      }
    }
  };

  return (
    <div className="position-layout">
      <select
        aria-label="Position Selection"
        id="position-selector"
        className="position-select button is-light"
        value={selectedSetup}
        onChange={handleOptionChange}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            // Try showPicker() if available, otherwise simulate click
            const selectElement = e.currentTarget as HTMLSelectElement;
            if (
              "showPicker" in selectElement &&
              typeof selectElement.showPicker === "function"
            ) {
              try {
                selectElement.showPicker();
              } catch (error) {
                // Fallback to click if showPicker fails
                selectElement.click();
              }
            } else {
              selectElement.click();
            }
          }
        }}
      >
        {/* Custom option - always visible */}
        <option key={CUSTOM_SETUP_ID} value={CUSTOM_SETUP_ID}>
          Custom
        </option>

        {/* Regular setups (no category) */}
        {CHESS_SETUPS.filter((setup) => !setup.category).map((setup) => (
          <option key={setup.id} value={setup.id}>
            {setup.displayName}
          </option>
        ))}

        {/* Categorized setups */}
        {Array.from(
          new Set(CHESS_SETUPS.map((s) => s.category).filter(Boolean)),
        ).map((category) => (
          <optgroup key={category} label={category}>
            {getSetupsByCategory(category!).map((setup) => (
              <option key={setup.id} value={setup.id}>
                {setup.displayName}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
};

export default SelectPosition;
